// Mini-descriptifs dépliables par module (FR/ES). Affichés sur l'écran d'accueil de chaque
// module et sur l'écran d'invitation patient (/p/...). short = 2-3 lignes toujours visibles ;
// detail = points complémentaires révélés via « En savoir plus ».

export const MODULE_INTRO_T = {
  fr: { more: "En savoir plus", less: "Réduire" },
  es: { more: "Saber más", less: "Reducir" },
};

export const MODULE_DESCRIPTIONS = {
  besoins: {
    short: {
      fr: "Un temps de réflexion sur vos 10 besoins psychologiques fondamentaux : à quel point ils sont satisfaits aujourd'hui et l'importance que vous leur accordez.",
      es: "Un momento de reflexión sobre sus 10 necesidades psicológicas fundamentales: hasta qué punto están satisfechas hoy y la importancia que les concede.",
    },
    detail: {
      fr: [
        "Vous évaluez chaque besoin sur une échelle simple, sans bonne ni mauvaise réponse.",
        "Une restitution visuelle (radar) met en regard satisfaction et importance.",
        "La synthèse est établie par règles cliniques — elle éclaire la discussion, sans poser de diagnostic.",
      ],
      es: [
        "Evalúa cada necesidad con una escala sencilla, sin respuestas buenas ni malas.",
        "Una devolución visual (radar) compara satisfacción e importancia.",
        "La síntesis se basa en reglas clínicas: orienta la conversación, sin emitir un diagnóstico.",
      ],
    },
  },
  "idr-50": {
    short: {
      fr: "50 affirmations pour explorer vos « drivers » relationnels — ces messages intérieurs (Sois parfait, Fais plaisir…) qui guident vos comportements et peuvent générer une pression interne.",
      es: "50 afirmaciones para explorar sus «drivers» relacionales: esos mensajes internos (Sé perfecto, Complace…) que guían sus comportamientos y pueden generar presión interna.",
    },
    detail: {
      fr: [
        "Pour chaque affirmation, vous indiquez votre niveau de correspondance sur un curseur (0 %, 25 %, 50 %, 75 %, 100 %), en 5 pages de 10 affirmations.",
        "Résultat : un profil des 5 drivers (score en %), un radar et une lecture clinique.",
        "Aucun score n'apparaît pendant la passation ; tout est présenté à la fin.",
      ],
      es: [
        "Para cada afirmación, indicas tu nivel de correspondencia en un control deslizante (0 %, 25 %, 50 %, 75 %, 100 %), en 5 páginas de 10 afirmaciones.",
        "Resultado: un perfil de los 5 drivers (puntuación en %), un radar y una lectura clínica.",
        "Ninguna puntuación aparece durante la realización; todo se presenta al final.",
      ],
    },
  },
  "qicm-20": {
    short: {
      fr: "20 affirmations pour observer votre rapport à vos propres pensées : capacité d'auto-réflexion, degré de certitude et souplesse cognitive.",
      es: "20 afirmaciones para observar su relación con sus propios pensamientos: capacidad de autorreflexión, grado de certeza y flexibilidad cognitiva.",
    },
    detail: {
      fr: [
        "Un questionnaire court qui éclaire l'insight cognitif et la métacognition.",
        "La restitution distingue auto-réflexivité, certitude et flexibilité.",
        "Outil de réflexion accompagné par le praticien, sans visée diagnostique.",
      ],
      es: [
        "Un cuestionario breve que ilumina el insight cognitivo y la metacognición.",
        "La devolución distingue autorreflexión, certeza y flexibilidad.",
        "Herramienta de reflexión acompañada por el profesional, sin fin diagnóstico.",
      ],
    },
  },
  "cope-28": {
    short: {
      fr: "28 affirmations pour repérer les stratégies que vous mobilisez face à une situation stressante précise (14 façons de faire face).",
      es: "28 afirmaciones para identificar las estrategias que moviliza ante una situación estresante concreta (14 formas de afrontar).",
    },
    detail: {
      fr: [
        "Vous ciblez d'abord une situation, puis vous répondez en la gardant à l'esprit.",
        "La restitution montre vos stratégies dominantes d'adaptation au stress.",
        "Disponible en français et en espagnol.",
      ],
      es: [
        "Primero elige una situación y luego responde teniéndola presente.",
        "La devolución muestra sus estrategias dominantes de afrontamiento del estrés.",
        "Disponible en francés y en español.",
      ],
    },
  },
  "drivers-exo": {
    short: {
      fr: "Un cahier d'exercices écrit, complémentaire à l'inventaire des drivers relationnels : pour chaque driver, vous découvrez son fonctionnement puis racontez vos propres situations.",
      es: "Un cuaderno de ejercicios escrito, complementario al inventario de drivers relacionales: para cada driver, descubre su funcionamiento y luego relata sus propias situaciones.",
    },
    detail: {
      fr: [
        "Cinq drivers passés en revue, avec des « antidotes » concrets à explorer.",
        "En fin de parcours, vous établissez votre propre classement des drivers.",
        "Votre rédaction en cours est conservée sur cet appareil pour être reprise plus tard.",
      ],
      es: [
        "Cinco drivers revisados, con «antídotos» concretos para explorar.",
        "Al final del recorrido, establece su propia clasificación de los drivers.",
        "Su redacción en curso se conserva en este dispositivo para retomarla más tarde.",
      ],
    },
  },
  empathie: {
    short: {
      fr: "Un exercice de couple : chacun devine ce qu'il croit savoir de l'autre, puis compare avec la réalité de son partenaire. Les écarts deviennent des ponts à construire.",
      es: "Un ejercicio de pareja: cada uno adivina lo que cree saber del otro y luego lo compara con la realidad de su pareja. Las diferencias se convierten en puentes por construir.",
    },
    detail: {
      fr: [
        "Six dimensions explorées : meilleurs moments, fiertés, plaisirs, frustrations, ce qu'on a besoin d'entendre…",
        "Réalisable sur un seul appareil (à tour de rôle) ou chacun sur son téléphone via un code.",
        "Un travail optionnel sur la frustration et un exercice « Est-ce que je te connais vraiment ? » viennent en complément.",
      ],
      es: [
        "Seis dimensiones exploradas: mejores momentos, orgullos, placeres, frustraciones, lo que se necesita oír…",
        "Se puede hacer en un solo dispositivo (por turnos) o cada uno en su teléfono mediante un código.",
        "Un trabajo opcional sobre la frustración y un ejercicio «¿Te conozco de verdad?» lo complementan.",
      ],
    },
  },
  "besoins-couple": {
    short: {
      fr: "La version couple de l'évaluation des besoins : chacun mesure la satisfaction de ses besoins et estime celle de l'autre, pour voir à quel point vous vous connaissez.",
      es: "La versión en pareja de la evaluación de necesidades: cada uno mide la satisfacción de sus necesidades y estima la del otro, para ver hasta qué punto se conocen.",
    },
    detail: {
      fr: [
        "Sur un seul appareil (à tour de rôle) ou chacun sur son téléphone via un code de séance.",
        "La restitution compare vos réalités et met en lumière vos angles morts.",
        "Disponible en français et en espagnol.",
      ],
      es: [
        "En un solo dispositivo (por turnos) o cada uno en su teléfono mediante un código de sesión.",
        "La devolución compara sus realidades y revela sus puntos ciegos.",
        "Disponible en francés y en español.",
      ],
    },
  },
  connais: {
    short: {
      fr: "Un jeu de découverte inspiré de Gottman : chacun devine les réponses de l'autre sur de nombreux thèmes, puis vous comparez et mesurez votre « score de curiosité ».",
      es: "Un juego de descubrimiento inspirado en Gottman: cada uno adivina las respuestas del otro sobre muchos temas, y luego comparan y miden su «puntuación de curiosidad».",
    },
    detail: {
      fr: [
        "12 parties, une cinquantaine de questions et une question bonus.",
        "Le « score de curiosité » mesure l'envie de connaître l'autre — ce n'est pas une note sur la qualité du couple.",
        "Sur un seul appareil ou chacun sur son téléphone ; FR / ES.",
      ],
      es: [
        "12 partes, una cincuentena de preguntas y una pregunta extra.",
        "La «puntuación de curiosidad» mide las ganas de conocer al otro; no es una nota sobre la calidad de la pareja.",
        "En un solo dispositivo o cada uno en su teléfono; FR / ES.",
      ],
    },
  },
  "schemas-pensees": {
    short: {
      fr: "Un cahier TCC pour repérer vos « pensées automatiques » et 10 distorsions cognitives, puis apprendre à les questionner et à les rééquilibrer à partir de situations vécues.",
      es: "Un cuaderno TCC para detectar tus «pensamientos automáticos» y 10 distorsiones cognitivas, y aprender a cuestionarlos y reequilibrarlos a partir de situaciones vividas.",
    },
    detail: {
      fr: [
        "Pour chaque distorsion : définition, auto-repérage, une situation vécue, la pensée et l'émotion, puis des questions pour prendre du recul.",
        "Démarche en trois temps : identifier, calmer (questionner), désactiver (restructurer).",
        "Aucune bonne ou mauvaise réponse ; à la fin, une synthèse et un export PDF.",
      ],
      es: [
        "Para cada distorsión: definición, auto-detección, una situación vivida, el pensamiento y la emoción, y luego preguntas para tomar distancia.",
        "Proceso en tres tiempos: identificar, calmar (cuestionar), desactivar (reestructurar).",
        "No hay respuestas correctas o incorrectas; al final, una síntesis y una exportación en PDF.",
      ],
    },
  },
  "programme-3-semaines": {
    short: {
      fr: "Un carnet de 3 semaines, 5 minutes par jour, pour réduire la charge mentale : d'abord observer son quotidien, puis analyser ses habitudes, enfin les ajuster.",
      es: "Un cuaderno de 3 semanas, 5 minutos al día, para reducir la carga mental: primero observar el día a día, luego analizar los hábitos y por último ajustarlos.",
    },
    detail: {
      fr: [
        "Chaque jour : de courtes check-lists, un niveau d'énergie (0–10) et une petite victoire.",
        "Des bilans hebdomadaires, la définition de 3 priorités et une boîte à outils concrète.",
        "Votre progression est conservée sur cet appareil ; un export PDF récapitule tout le carnet.",
      ],
      es: [
        "Cada día: listas breves, un nivel de energía (0–10) y una pequeña victoria.",
        "Balances semanales, la definición de 3 prioridades y una caja de herramientas concreta.",
        "Su progreso se conserva en este dispositivo; una exportación en PDF resume todo el cuaderno.",
      ],
    },
  },
  diva5: {
    short: {
      fr: "Préparez l'entretien DIVA-5 : pour chacun des 18 critères (attention, hyperactivité-impulsivité), cochez les exemples qui vous correspondent à l'âge adulte et dans l'enfance. Un proche peut remplir sa propre version.",
      es: "Prepare la entrevista DIVA-5: para cada uno de los 18 criterios, marque los ejemplos que le corresponden en la edad adulta y en la infancia. Un allegado puede completar su propia versión.",
    },
    detail: {
      fr: ["18 critères, chacun avec ses exemples « âge adulte » (6 derniers mois) et « enfance » (5-12 ans) et un avis indicatif oui/non.", "La cotation finale (symptôme présent, critères B, C, E, retentissement) est réalisée par le praticien lors de l'entretien : le PDF contient la grille.", "Aucune donnée n'est transmise : vous téléchargez le PDF et le remettez à votre praticien."],
      es: ["18 criterios con ejemplos para la edad adulta y la infancia.", "La cotación final la realiza el profesional.", "Ningún dato se transmite: descargue el PDF."],
    },
  },
  assq: {
    short: {
      fr: "Autism Spectrum Screening Questionnaire (Ehlers, Gillberg & Wing, 1999) : 27 questions Non / Un peu / Oui pour repérer les caractéristiques évocatrices d'un TSA chez l'enfant ou l'adolescent, à remplir par un parent. Aucun diagnostic.",
      es: "Autism Spectrum Screening Questionnaire (Ehlers, Gillberg & Wing, 1999): 27 preguntas para detectar rasgos evocadores de TEA en niños y adolescentes, respondido por un padre/madre.",
    },
    detail: {
      fr: ["Pour chaque question : exemples pour comprendre (sans orienter la réponse) et champ facultatif « situation concrète ».", "Score total 0–54 avec seuils 11 / 19, quatre sous-échelles (style formel, compréhension contextuelle et routines, réciprocité socio-émotionnelle, vocalisation-mouvement-apparence), synthèse descriptive.", "Architecture 100 % locale : réponses et scores calculés et conservés uniquement dans le navigateur (IndexedDB). Synthèse PDF à télécharger, imprimer ou transmettre par action volontaire ; suppression des données en un clic."],
      es: ["Ejemplos para comprender cada pregunta y campo opcional « situación concreta ».", "Puntuación 0–54 con umbrales 11 / 19, cuatro subescalas y síntesis descriptiva.", "Arquitectura 100 % local: respuestas en el navegador; PDF descargable, transmisión solo voluntaria."],
    },
  },
  conners3: {
    short: {
      fr: "L'échelle Conners 3 pour le parent : 108 items décrivant votre enfant au cours du dernier mois (0 = pas du tout vrai … 3 = très vrai) et 2 questions ouvertes. Chaque parent remplit sa version.",
      es: "La escala Conners 3 para padres: 108 ítems sobre su hijo/a durante el último mes (0 = nada cierto … 3 = muy cierto) y 2 preguntas abiertas.",
    },
    detail: {
      fr: ["9 pages de 12 items, une seule réponse par item ; il est important de répondre à tous.", "Le PDF liste vos réponses item par item avec des regroupements indicatifs (sans normes) pour la lecture du praticien.", "Vos réponses restent sur votre appareil."],
      es: ["9 páginas de 12 ítems, una sola respuesta por ítem.", "El PDF lista sus respuestas ítem por ítem.", "Sus respuestas permanecen en su dispositivo."],
    },
  },
  "tdah-parents": {
    short: {
      fr: "Recueil séparé de la perception de chaque parent sur le fonctionnement quotidien de l'enfant (TDAH présent ou suspecté), en 10 sections et 15–20 minutes. Résultats réservés au professionnel.",
      es: "Recogida separada de la percepción de cada progenitor sobre el funcionamiento cotidiano del niño (TDAH presente o sospechado), en 10 secciones y 15–20 minutos. Resultados reservados al profesional.",
    },
    detail: {
      fr: ["Deux liens personnels, un par parent ; aucun parent ne voit les réponses de l'autre.", "Comparaison des perceptions par domaine, convergences / divergences et questions d'entretien, export PDF.", "Outil d'aide à l'entretien clinique : aucune conclusion diagnostique automatique."],
      es: ["Dos enlaces personales, uno por progenitor; ninguno ve las respuestas del otro.", "Comparación de percepciones por dominio, convergencias / divergencias y preguntas de entrevista, exportación PDF.", "Herramienta de apoyo a la entrevista clínica: sin conclusión diagnóstica automática."],
    },
  },
  "guide-tdah": {
    short: {
      fr: "Un guide pratique, simple et illustré pour comprendre le fonctionnement TDAH et soutenir l'enfant à l'école comme à la maison : 16 chapitres courts, à lire un à la fois, avec PDF complet et fiches réflexes imprimables.",
      es: "Una guía práctica, sencilla e ilustrada para comprender el funcionamiento TDAH y apoyar al niño en la escuela y en casa: 16 capítulos breves, con PDF completo y fichas imprimibles.",
    },
    detail: {
      fr: ["Les bases (fonctions exécutives, aides externes, séquence d'intervention), 10 leviers en classe, gestion de la frustration, organisation à la maison, devoirs, autonomie.", "7 exercices pour l'enfant, actions et phrases à favoriser / à éviter, fiches réflexes décrochage et frustration, suivi des progrès.", "Lecture confortable (texte agrandi), progression enregistrée sur l'appareil, aucune donnée transmise."],
      es: ["Bases, 10 palancas en clase, gestión de la frustración, organización en casa, deberes, autonomía.", "7 ejercicios para el niño, acciones y frases a favorecer / evitar, fichas reflejo.", "Lectura cómoda, progreso guardado en el dispositivo, ningún dato transmitido."],
    },
  },
  autogratification: {
    short: {
      fr: "Apprendre à reconnaître ce que je fais bien, mes efforts et mes petites réussites : un parcours quotidien de 3 à 5 minutes, sans nier ce qui a été difficile, puis une autogratification choisie. Bilan du jour et semaine positive.",
      es: "Aprender a reconocer lo que hago bien, mis esfuerzos y mis pequeños logros: un recorrido diario de 3 a 5 minutos, sin negar lo difícil, y una autogratificación elegida.",
    },
    detail: {
      fr: ["Ce qui a été difficile → ce qui a fonctionné → mes efforts → mes petites victoires → ce que je reconnais chez moi → est-ce que je minimise le positif ? → mon autogratification → ma phrase du jour.", "Deux repères de suivi (0–10) sans valeur diagnostique, bilan « Ma journée en 4 éléments », « Ma semaine positive » avec courbe sur 7 jours et ressources les plus reconnues.", "Aucune question obligatoire, aucune positivité forcée, aucun diagnostic. Journées conservées sur l'appareil ; synthèse de séance téléchargeable et partage uniquement à votre initiative."],
      es: ["Lo difícil → lo que funcionó → mis esfuerzos → mis pequeñas victorias → lo que reconozco en mí → ¿minimizo lo positivo? → mi autogratificación → mi frase del día.", "Dos indicadores de seguimiento (0–10) sin valor diagnóstico, balance diario y semana positiva con curva de 7 días.", "Nada obligatorio, sin positividad forzada, sin diagnóstico. Datos en el dispositivo; compartir solo por tu iniciativa."],
    },
  },
  "sujets-therapeutiques": {
    short: {
      fr: "Préparez votre séance en quelques minutes : notez les sujets qui vous préoccupent avec vos mots, évaluez leur importance et leur priorité (1 à 4), et obtenez une synthèse claire des sujets à aborder en priorité.",
      es: "Prepara tu sesión en pocos minutos: anota los temas que te preocupan, evalúa su importancia y prioridad (1 a 4) y obtén una síntesis clara.",
    },
    detail: {
      fr: ["Un sujet à la fois : vous l'écrivez, puis deux questions simples (importance, priorité). Le niveau d'urgence thérapeutique est calculé automatiquement.", "Synthèse : classement, cartographie, sujet principal, objectif pour la séance et phrase modifiable « Aujourd'hui, j'aimerais travailler sur… ».", "Outil de préparation, jamais de diagnostic. Téléchargement PDF et partage uniquement à votre initiative ; historique sur cet appareil."],
      es: ["Un tema a la vez, dos preguntas sencillas. El nivel de urgencia terapéutica se calcula automáticamente.", "Síntesis: clasificación, mapa, tema principal y objetivo para la sesión.", "Herramienta de preparación, nunca de diagnóstico. PDF y compartir solo por tu iniciativa."],
    },
  },
  "boite-outils-tdah": {
    short: {
      fr: "Préconisations scolaires centrées sur les solutions pour les élèves avec TDAH : 20 fiches à favoriser / à éviter, boîte à outils rapide, 10 règles d'or et formulation pour un compte rendu neuropsychologique. Téléchargeable en PDF.",
      es: "Recomendaciones escolares centradas en soluciones para alumnos con TDAH: 20 fichas, caja de herramientas rápida, 10 reglas de oro y formulación para un informe. Descargable en PDF.",
    },
    detail: {
      fr: ["Attention, consignes, mémoire de travail, organisation, démarrage, tâches longues, évaluations, devoirs, temps, transitions.", "Impulsivité, agitation motrice, motivation, autonomie, correction des erreurs, comportements, communication, coordination école–famille.", "Destinée aux enseignants, aux familles et aux comptes rendus : aucune donnée saisie, lecture libre."],
      es: ["Atención, consignas, memoria de trabajo, organización, inicio, tareas largas, evaluaciones, deberes, tiempo, transiciones.", "Impulsividad, agitación motriz, motivación, autonomía, errores, conductas, comunicación, coordinación escuela–familia.", "Lectura libre, sin datos introducidos."],
    },
  },
  "super-cerveau": {
    short: {
      fr: "Un jeu coopératif enfant-parent pour accompagner le TDAH au quotidien : chaque soir, une enquête de 3 à 5 minutes pour trouver ce qui aide, ce qui gêne, ce qui bloque… et choisir l'astuce de demain.",
      es: "Un juego cooperativo niño-padres para acompañar el TDAH en el día a día: cada noche, una investigación de 3 a 5 minutos para encontrar lo que ayuda, lo que molesta, lo que bloquea… y elegir el arma de mañana.",
    },
    detail: {
      fr: [
        "4 cartes : Super-pouvoir, Petit obstacle, La montagne infranchissable, Nouvelle astuce — listes personnalisables.",
        "Des XP gagnés en utilisant une stratégie (jamais pour être « sage »), un menu de récompenses construit avec l'enfant et le défi « Zéro rappel ».",
        "Le dimanche : conseil des super-cerveaux, laboratoire des armes, contrat de la semaine et cérémonie des héros, exportables en PDF.",
        "La progression est conservée sur cet appareil ; le parent reste le maître du jeu.",
      ],
      es: [
        "4 cartas: Superpoder, Pequeño obstáculo, Jefe final, Nueva arma — listas personalizables.",
        "XP ganados usando una estrategia, un menú de recompensas construido con el niño y el reto « Cero recordatorios ».",
        "El domingo: consejo, laboratorio, contrato de la semana y ceremonia de los héroes, exportables en PDF.",
        "El progreso se guarda en este dispositivo; el padre sigue siendo el maestro del juego.",
      ],
    },
  },
};
